import { PageHeader, PaginationPageDefault } from "@components";
import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { Suspense, useState } from "react";
import {
	type ExpenseModalMode,
	ExpensesExportModal,
	ExpensesFilteredHistoryTable,
	ExpensesHistoryFilters,
	type ExpensesHistoryFiltersState,
	NewExpenseModal,
} from "../components";
import { useGetExpensesHistory } from "../hooks";

const now = today(getLocalTimeZone());
const startDate = new CalendarDate(now.year, 1, 1);
const HISTORY_PAGE_SIZE = 10;

const defaultFilters: ExpensesHistoryFiltersState = {
	plate: "",
	startDate,
	endDate: now,
};

function buildSubtitle(
	totalCount: number | undefined,
	isFetching: boolean
): string | undefined {
	if (totalCount !== undefined) {
		return totalCount === 1
			? "1 despesa encontrada"
			: `${totalCount} despesas encontradas`;
	}

	return isFetching ? "Carregando despesas" : undefined;
}

function ExpensesHistoryPageContent() {
	const [historyPage, setHistoryPage] = useState(1);
	const [isExportModalOpen, setIsExportModalOpen] = useState(false);

	const [filters, setFilters] =
		useState<ExpensesHistoryFiltersState>(defaultFilters);

	const criteria = {
		startDate: filters?.startDate?.toString() || "",
		endDate: filters?.endDate?.toString() || "",
		...(filters?.plate && { plate: filters?.plate }),
		...(filters?.types?.length && { types: filters.types }),
		pageSize: HISTORY_PAGE_SIZE,
	};

	const historyQuery = useGetExpensesHistory({
		...criteria,
		pageNumber: historyPage,
	});
	const { data, isFetching, isError, refetch } = historyQuery;

	const { total: totalPages } = usePaginationTotal({
		page: historyPage,
		onPageChange: setHistoryPage,
		criteria,
		totalPages: data?.totalPages,
		query: historyQuery,
	});

	const [expenseModal, setExpenseModal] = useState<{
		mode: ExpenseModalMode;
		id: string | null;
	}>({ mode: "view", id: null });
	const isModalOpen = expenseModal.id !== null;

	return (
		<div className="flex flex-col gap-6 px-6 py-8">
			<PageHeader
				subtitle={buildSubtitle(data?.totalCount, isFetching)}
				title="Histórico de Despesas"
			/>

			<ExpensesHistoryFilters
				filters={filters}
				onExport={() => setIsExportModalOpen(true)}
				onFiltersChange={setFilters}
				onPageChange={setHistoryPage}
			/>

			<ExpensesFilteredHistoryTable
				footer={
					totalPages !== null && totalPages > 1 ? (
						<PaginationPageDefault
							className="p-5"
							onPageChange={setHistoryPage}
							page={historyPage}
							total={totalPages}
						/>
					) : null
				}
				isError={isError}
				isLoading={isFetching}
				items={data?.items ?? []}
				onEdit={setExpenseModal}
				onRetry={() => {
					refetch();
				}}
			/>

			<NewExpenseModal
				id={expenseModal.id}
				isOpen={isModalOpen}
				mode={expenseModal.mode}
				onClose={() => {
					setExpenseModal({ mode: "view", id: null });
				}}
				onSuccess={() => {
					setExpenseModal({ mode: "view", id: null });
				}}
			/>

			<ExpensesExportModal
				isOpen={isExportModalOpen}
				onClose={() => setIsExportModalOpen(false)}
			/>
		</div>
	);
}

export default function ExpensesHistoryPage() {
	return (
		<Suspense
			fallback={<div className="p-6 text-secondary text-sm">Carregando...</div>}
		>
			<ExpensesHistoryPageContent />
		</Suspense>
	);
}
